/* eslint-disable jsx-a11y/control-has-associated-label */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { useMutation } from '@tanstack/react-query'

import { USER_STATUS } from '../../lib/const'
import { apiErrorMessage, capitalizeFirstLetter, setAuth } from '../../lib/utils'
import httpClient from '../../httpClient'
import { usePendingPhotographersCount, usePhotographers } from '../../stores'
import CustomTooltip from '../common/Tooltip'

import { ReactComponent as WorldIcon } from '../../images/icons/world.svg'
import { ReactComponent as CheckRoundedIcon } from '../../images/icons/check-rounded.svg'
import { ReactComponent as LoginIcon } from '../../images/icons/login.svg'
import { ReactComponent as ChevronDownIcon } from '../../images/icons/chevron-down.svg'

// photographers

const USER_STATUS_PAYLOADS = {
  [USER_STATUS.approved]: { userApproved: true, userDeclined: false },
  [USER_STATUS.declined]: { userApproved: false, userDeclined: true },
  [USER_STATUS.pending]: { userApproved: false, userDeclined: false },
}
const USER_STATUSES = Object.values(USER_STATUS)

const getUserStatus = (user) => {
  if (user?.userApproved) return USER_STATUS.approved
  if (user?.userDeclined) return USER_STATUS.declined

  return USER_STATUS.pending
}

const PhotographersDatabaseRow = ({ user }) => {
  const navigate = useNavigate()
  const businessUrl = user?.businessUrl?.trim()
  const websiteUrl =
    businessUrl && /^https?:\/\//i.test(businessUrl) ? businessUrl : `https://${businessUrl}`
  const escapedWebsiteUrl = websiteUrl
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  const websiteTooltip = businessUrl
    ? `<span style="color:#0000FF;">${escapedWebsiteUrl}</span>`
    : 'No website or business link'
  const userStatus = getUserStatus(user)
  const [selectedStatus, setSelectedStatus] = useState(userStatus)
  const [statusError, setStatusError] = useState(null)
  const [loginError, setLoginError] = useState(null)
  const fetchPhotographers = usePhotographers((state) => state.fetchPhotographers)
  const fetchCounts = usePendingPhotographersCount((state) => state.fetchCounts)
  const updateStatus = useMutation({
    mutationFn: (newStatus) =>
      httpClient.patch(`photographers/${user.guid}`, USER_STATUS_PAYLOADS[newStatus]),
    onSuccess: async () => {
      setStatusError(null)
      await Promise.all([fetchPhotographers(), fetchCounts(['pending', 'declined'], true)])
    },
    onError: (error) => {
      setSelectedStatus(userStatus)
      setStatusError(apiErrorMessage(error))
    },
  })
  const loginAsUser = useMutation({
    mutationFn: () => httpClient.post('login_as_user', { email: user.email }),
    onSuccess: (auth) => {
      setAuth({
        ...auth,
        is_user_admin: auth.isUserAdmin ?? auth.is_user_admin,
      })
      navigate('/lab-orders')
    },
    onError: (error) => setLoginError(apiErrorMessage(error)),
  })

  useEffect(() => setSelectedStatus(userStatus), [userStatus])

  const handleStatusChange = (event) => {
    const newStatus = event.target.value

    setSelectedStatus(newStatus)
    setStatusError(null)
    updateStatus.mutate(newStatus)
  }

  const handleLoginAsUser = (event) => {
    event.preventDefault()

    if (!loginAsUser.isLoading) {
      setLoginError(null)
      loginAsUser.mutate()
    }
  }

  return (
    <div className="w-layout-grid grid-table-heady">
      <div className="content name">
        <div>
          <div className="w-layout-grid grid-23">
            <div className="text-table name">{`${user?.firstName} ${user?.lastName}`}</div>
            <Link to="/photographer-profile" className="link-block-7 w-inline-block">
              {/* <div className="icon small x2 w-embed">
                <AccountIcon />
              </div> */}
            </Link>
            <CustomTooltip title={websiteTooltip}>
              <a
                href={businessUrl ? websiteUrl : undefined}
                className="link-block-7 w-inline-block"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${user?.firstName || 'photographer'}'s website`}
              >
                <div className="icon small x2 w-embed">
                  <WorldIcon />
                </div>
              </a>
            </CustomTooltip>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="text-table">{user?.email}</div>
      </div>
      <div className="content">
        <div className="text-table-top-container">
          <div className="text-table">{user?.businessName ?? '-'}</div>
        </div>
      </div>
      <div className="content">
        <div className="text-table-top-container">
          <div className="text-table">?</div>
        </div>
      </div>
      <div className="content">
        <div className="text-table">?</div>
      </div>
      <div id="w-node-_79f08531-6afd-7b9d-2f32-6719dcb29f77-67e80a11" className="content">
        <div
          className={cn('status-admin-photographer', {
            unapproved: selectedStatus === USER_STATUS.declined,
            pending: selectedStatus === USER_STATUS.pending,
          })}
        >
          <div className="icon small x2 right-margin less w-embed">
            <CheckRoundedIcon />
          </div>
          <div>{capitalizeFirstLetter(selectedStatus)}</div>
          <ChevronDownIcon className="status-admin-photographer-chevron" aria-hidden="true" />
          <select
            className="status-admin-photographer-select"
            value={selectedStatus}
            onChange={handleStatusChange}
            disabled={updateStatus.isLoading}
            aria-label={`Change status for ${user?.firstName} ${user?.lastName}`}
            aria-invalid={Boolean(statusError)}
            title={statusError || undefined}
          >
            {USER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {capitalizeFirstLetter(status)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div id="w-node-_79f08531-6afd-7b9d-2f32-6719dcb29f7f-67e80a11" className="empty-div">
        <button
          type="button"
          className="overflow-menu__link login-as-user-button"
          onClick={handleLoginAsUser}
          disabled={loginAsUser.isLoading}
          title={loginError || undefined}
        >
          <div className="icon small right-margin w-embed">
            <LoginIcon />
          </div>
          <div>Login as User</div>
        </button>
      </div>
    </div>
  )
}

export default PhotographersDatabaseRow
